// ManDana - per-line writing direction
//
// Browsers do not expose the active software-keyboard language, but
// `beforeinput` exposes the text before it is painted. The editor uses these
// helpers to give each line the direction of its first strong character and to
// carry the latest detected writing direction onto a newly-created blank line.

export type MdWritingDirection = 'ltr' | 'rtl';

const MD_RTL_RANGES = /[\u0590-\u08ff\ufb1d-\ufdff\ufe70-\ufefc]/u;
const MD_LETTER = /\p{Letter}/u;

/** The Unicode bidi direction established by the first strong letter. */
function mdStrongDirection(value: unknown): MdWritingDirection | null {
  for (const char of String(value == null ? '' : value)) {
    if (!MD_LETTER.test(char)) continue;
    return MD_RTL_RANGES.test(char) ? 'rtl' : 'ltr';
  }
  return null;
}

/** The zero-based source line containing a caret offset. */
function mdDirectionLineAt(source: unknown, offset: unknown): number {
  const text = String(source == null ? '' : source);
  const end = Math.max(0, Math.min(text.length,
    Number.isFinite(offset) ? Number(offset) : text.length));
  let line = 0;
  for (let i = 0; i < end; i++) if (text.charCodeAt(i) === 10) line++;
  return line;
}

/** The changed run in the new text, excluding its shared prefix and suffix. */
function mdInsertedText(before: unknown, after: unknown): string {
  const prev = String(before == null ? '' : before);
  const next = String(after == null ? '' : after);
  let start = 0;
  const shared = Math.min(prev.length, next.length);
  while (start < shared && prev.charCodeAt(start) === next.charCodeAt(start)) start++;
  let prevEnd = prev.length;
  let nextEnd = next.length;
  while (prevEnd > start && nextEnd > start
    && prev.charCodeAt(prevEnd - 1) === next.charCodeAt(nextEnd - 1)) {
    prevEnd--;
    nextEnd--;
  }
  return next.slice(start, nextEnd);
}

/** Resolve every line, retaining explicit hints only while a line is neutral. */
function mdLineDirections(source: unknown, hints: unknown = [],
  fallback: MdWritingDirection = 'ltr'): MdWritingDirection[] {
  const prior = Array.isArray(hints) ? hints : [];
  const base: MdWritingDirection = fallback === 'rtl' ? 'rtl' : 'ltr';
  return String(source == null ? '' : source).split('\n').map((line, index) => {
    const strong = mdStrongDirection(line);
    if (strong) return strong;
    return prior[index] === 'rtl' || prior[index] === 'ltr' ? prior[index] : base;
  });
}

/**
 * Keep neutral-line hints aligned when an edit adds or removes line breaks.
 * New lines inherit the current writing direction; joined lines keep the hint
 * of the line on the near side of the join. Strong text always wins afterward.
 */
function mdReconcileLineDirections(before: unknown, after: unknown, hints: unknown,
  editOffset: unknown, writingDirection: MdWritingDirection = 'ltr'): MdWritingDirection[] {
  const prev = String(before == null ? '' : before);
  const next = String(after == null ? '' : after);
  const directions = mdLineDirections(prev, hints, writingDirection);
  const delta = next.split('\n').length - prev.split('\n').length;
  const line = mdDirectionLineAt(prev, editOffset);
  if (delta > 0) directions.splice(line + 1, 0, ...Array(delta).fill(writingDirection));
  else if (delta < 0) directions.splice(line + 1, -delta);
  return mdLineDirections(next, directions, writingDirection);
}

Object.assign(window, {
  mdStrongDirection,
  mdDirectionLineAt,
  mdInsertedText,
  mdLineDirections,
  mdReconcileLineDirections,
});
